import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { api } from "@/lib/api";
import { Phone, PhoneForwarded, Check } from "lucide-react";

export default function Settings() {
  const { user, refresh } = useAuth();
  const { t, lang, setLang } = useI18n();
  const L = lang === "el";
  const [phone, setPhone] = useState("");
  const [forwardWhenOffline, setForwardWhenOffline] = useState(true);
  const [savingCall, setSavingCall] = useState(false);
  const [savedNotice, setSavedNotice] = useState(false);

  useEffect(() => {
    if (!user) return;
    setPhone(user.phone || "");
    setForwardWhenOffline(user.forward_when_offline !== false);
  }, [user]);

  const updateLang = async (l) => {
    setLang(l);
    try {
      await api.patch(`/users/${user.id}`, { language: l });
      refresh();
    } catch {}
  };

  const saveCallSettings = async () => {
    setSavingCall(true);
    try {
      await api.patch(`/users/${user.id}`, {
        phone: phone || null,
        forward_when_offline: forwardWhenOffline,
      });
      await refresh();
      setSavedNotice(true);
      setTimeout(() => setSavedNotice(false), 2500);
    } catch { /* silent */ }
    finally { setSavingCall(false); }
  };

  const canManageCallForwarding = user?.role === "company_owner" || user?.role === "sales_rep";

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{t("nav.settings")}</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mt-1">{L ? "Ρυθμίσεις" : "Settings"}</h1>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-6 space-y-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{L ? "Λογαριασμός" : "Account"}</div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Row label={t("common.name")} value={user?.name} />
            <Row label={t("common.email")} value={user?.email} />
            <Row label={L ? "Ρόλος" : "Role"} value={user?.role?.replace("_", " ")} />
            <Row label={t("common.country")} value={user?.country || "—"} />
          </div>
        </div>
      </div>

      {canManageCallForwarding && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-6 space-y-4" data-testid="settings-call-forwarding">
          <div className="flex items-center gap-2">
            <PhoneForwarded className="w-4 h-4 text-slate-500" />
            <div className="text-sm font-heading font-bold text-slate-900">{L ? "Προώθηση κλήσεων" : "Call forwarding"}</div>
          </div>
          <p className="text-xs text-slate-500">
            {L
              ? "Όταν κάποιος καλεί το εταιρικό νούμερο και δεν είστε συνδεδεμένοι στην πλατφόρμα, η κλήση θα προωθηθεί αυτόματα στο κινητό που θα δηλώσετε εδώ. Το τηλέφωνό σας παραμένει ιδιωτικό — δεν εμφανίζεται πουθενά στην πλατφόρμα."
              : "When someone calls your company number while you're offline, the call is auto-forwarded to the mobile below. Your phone stays private — it's never shown anywhere on the platform."}
          </p>

          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-700">
              {L ? "Κινητό για προώθηση" : "Forwarding mobile"}
            </label>
            <div className="mt-1.5 relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                data-testid="settings-forward-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+30 69..."
                className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-md text-sm"
              />
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              {L ? "Χρησιμοποιήστε διεθνή μορφή (π.χ. +306912345678)" : "Use international format (e.g. +306912345678)"}
            </div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              data-testid="settings-forward-toggle"
              type="checkbox"
              checked={forwardWhenOffline}
              onChange={(e) => setForwardWhenOffline(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#0052CC]"
            />
            <div>
              <div className="text-sm font-semibold text-slate-900">
                {L ? "Προώθηση όταν δεν είμαι online" : "Forward when I'm offline"}
              </div>
              <div className="text-xs text-slate-500">
                {L
                  ? "Απενεργοποιήστε αν δεν θέλετε να λαμβάνετε κλήσεις στο κινητό σας."
                  : "Turn off if you don't want calls on your mobile."}
              </div>
            </div>
          </label>

          <div className="flex items-center justify-between gap-3 pt-2">
            {savedNotice && (
              <div className="inline-flex items-center gap-1.5 text-emerald-700 text-xs font-semibold" data-testid="settings-saved-notice">
                <Check className="w-3.5 h-3.5" /> {L ? "Αποθηκεύτηκε" : "Saved"}
              </div>
            )}
            <button
              data-testid="settings-call-save"
              onClick={saveCallSettings}
              disabled={savingCall}
              className="ml-auto px-4 py-2 bg-[#0052CC] hover:bg-[#0043A8] text-white text-sm font-semibold rounded-md disabled:opacity-50"
            >
              {savingCall ? (L ? "Αποθήκευση..." : "Saving...") : (L ? "Αποθήκευση" : "Save")}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-6">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">{t("lang.switch")}</div>
        <div className="flex gap-2">
          <button data-testid="settings-lang-el" onClick={() => updateLang("el")} className={`px-4 py-2 rounded-md text-sm font-semibold border ${lang === "el" ? "bg-[#0052CC] text-white border-[#0052CC]" : "border-slate-200 text-slate-700"}`}>Ελληνικά</button>
          <button data-testid="settings-lang-en" onClick={() => updateLang("en")} className={`px-4 py-2 rounded-md text-sm font-semibold border ${lang === "en" ? "bg-[#0052CC] text-white border-[#0052CC]" : "border-slate-200 text-slate-700"}`}>English</button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{label}</div>
      <div className="mt-1 text-sm font-medium text-slate-900 break-words">{value}</div>
    </div>
  );
}
